import { useEffect, useRef } from 'react';

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    let frame = 0;
    let animId: number;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const spacing = 60;
      ctx.strokeStyle = 'rgba(56,189,248,0.07)';
      ctx.lineWidth = 1;
      for (let x = 0; x < canvas.width; x += spacing) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = 0; y < canvas.height; y += spacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
      // Animated dots
      const dots = [
        { x: 0.2, y: 0.3 }, { x: 0.5, y: 0.6 }, { x: 0.8, y: 0.2 },
        { x: 0.35, y: 0.75 }, { x: 0.65, y: 0.45 },
      ];
      dots.forEach((d, i) => {
        const pulse = Math.sin(frame * 0.02 + i * 1.2) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(d.x * canvas.width, d.y * canvas.height, 3 + pulse * 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(56,189,248,${0.2 + pulse * 0.4})`;
        ctx.fill();
      });
      frame++;
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToServices = () => {
    const el = document.getElementById('services');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center bg-[#0f1115] overflow-hidden"
    >
      {/* Grid canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* Glow blobs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-[#0ea5e9]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/3 w-64 h-64 rounded-full bg-[#38bdf8]/8 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-24 pb-16">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left */}
          <div className="flex-1 w-full">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#38bdf8]/30 bg-[#38bdf8]/5 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#38bdf8] animate-pulse" />
              <span className="text-[#7dd3fc] text-sm font-medium tracking-widest uppercase">Cloud Accounting SaaS</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              経理業務を、<br />
              <span className="bg-gradient-to-r from-[#38bdf8] to-[#0ea5e9] bg-clip-text text-transparent">
                クラウドで一括管理
              </span>
            </h1>

            <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
              売上・入出金・請求・支払情報をリアルタイムで可視化。<br />
              業務効率化を実現する次世代クラウド型経理システム。
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToContact}
                className="px-7 py-3 rounded-md bg-gradient-to-r from-[#0ea5e9] to-[#38bdf8] text-white font-semibold text-sm hover:opacity-90 transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                お問い合わせ
                <i className="ri-arrow-right-line ml-2" />
              </button>
              <button
                onClick={scrollToServices}
                className="px-7 py-3 rounded-md border border-[#38bdf8]/40 text-[#38bdf8] font-semibold text-sm hover:bg-[#38bdf8]/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                サービスを見る
              </button>
            </div>


          </div>

          {/* Right - Dashboard mockup */}
          <div className="flex-1 w-full max-w-lg lg:max-w-none">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0ea5e9]/20 to-[#38bdf8]/10 rounded-2xl blur-xl" />
              <div className="relative bg-[#1a1d23] border border-white/10 rounded-2xl overflow-hidden">
                {/* Window bar */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-[#13161b]">
                  <span className="w-3 h-3 rounded-full bg-red-500/70" />
                  <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
                  <span className="w-3 h-3 rounded-full bg-green-500/70" />
                  <span className="ml-4 text-gray-400 text-sm font-mono">accounting-dashboard.ks-capital.jp</span>
                </div>
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20dark%20cloud%20accounting%20dashboard%20with%20charts%20graphs%20revenue%20data%20visualization%20blue%20cyan%20color%20scheme%20professional%20SaaS%20interface%20clean%20minimal%20design%20with%20sidebar%20navigation%20and%20data%20tables&width=600&height=400&seq=hero-dashboard-001&orientation=landscape"
                  alt="経理ダッシュボード"
                  className="w-full h-64 md:h-80 object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-gray-400 text-sm">Scroll</span>
        <i className="ri-arrow-down-line text-gray-400" />
      </div>
    </section>
  );
}
